<template>
  <div class="address-section">
    <div class="row align-items-center">
      <label class="col-sm-3 control-label">
        <slot name="streetAddressLbl"> Street Address{{ required ? '*' : '' }} </slot>
      </label>
      <div class="col-sm-8 col-11">
        <address-autocomplete-input
          :id="`streetAddress-${pageId}`"
          v-model="address.streetAddress"
          :required="required"
          :anchor-bound="anchorBound"
          @place-changed="setAddressData"
        />
      </div>
      <div class="col-sm-1 col-1 no-padding">
        <el-tooltip
          v-if="showHomeAddressBtn"
          class="item"
          effect="dark"
          placement="top"
          popper-class="custom-tooltip"
        >
          <template #content>
            Same as Home Address
          </template>
          <button
            type="button"
            class="home"
            @click="setHomeAddress"
          >
            <i class="fa fa-home" />
          </button>
        </el-tooltip>
      </div>
    </div>

    <div
      v-if="showApt"
      class="row align-items-center"
    >
      <label class="col-sm-3 control-label"> Apt# </label>
      <div class="col-sm-3">
        <sanitized-input
          :id="`streetAddress2-${pageId}`"
          v-model="address.streetAddress2"
          placeholder="Apt number"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> City{{ required ? '*' : '' }} </label>
      <div class="col-sm-3">
        <sanitized-input
          :id="`city-${pageId}`"
          v-model="address.city"
          placeholder="Independence"
          :required="required"
        />
      </div>

      <label class="col-sm-2 control-label mobile-mt-10">
        {{ isFromCanada ? `Provinces${required ? '*' : ''}` : `State${required ? '*' : ''}` }}
      </label>
      <div class="col-sm-3">
        <sanitized-input
          :id="`state-${pageId}`"
          v-model="address.state"
          placeholder="KS"
          :required="required"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label">
        {{ isFromCanada ? `Postal${required ? '*' : ''}` : `Zip${required ? '*' : ''}` }}
      </label>
      <div class="col-sm-3">
        <sanitized-input
          :id="`zip-${pageId}`"
          v-model="address.zip"
          placeholder="67301"
          :required="required"
        />
      </div>

      <label class="col-sm-2 control-label mobile-mt-10"> Country{{ required ? '*' : '' }} </label>
      <div class="col-sm-3">
        <el-tooltip
          placement="right"
          :visible="showCountryTooltip"
        >
          <sanitized-input
            :id="`country-${pageId}`"
            v-model="address.country"
            placeholder="US"
            :required="required"
            @focus="showCountryTooltip = true"
            @blur="showCountryTooltip = false"
          />
          <template #content>
            Two-letter country code (e.g. US, CA, AU)
          </template>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, WritableComputedRef, PropType } from 'vue';

import { useCustomer } from '@/use/customer';
import { CustomLocationType } from '@/models/address';
import Cleave from 'cleave.js';

const props = defineProps({
  modelValue: {
    type: Object as PropType<CustomLocationType>,
    default: () => ({
      streetAddress: '',
      streetAddress2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    }),
  },

  pageId: {
    type: String,
    default: 'page-content',
  },

  showApt: {
    type: Boolean,
    default: true,
  },

  required: {
    type: Boolean,
    default: true,
  },

  showHomeAddressBtn: {
    type: Boolean,
    default: false,
  },
});

const showCountryTooltip = ref(false);

const emit = defineEmits(['update:modelValue', 'set-home-address']);

const { anchorBound } = useCustomer();

const address: WritableComputedRef<CustomLocationType> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const isFromCanada = computed(() => address.value.country === 'CA');

const setHomeAddress = () => emit('set-home-address');

const setAddressData = (address: CustomLocationType | null) => {
  emit('update:modelValue', {
    streetAddress: address?.streetAddress || '',
    streetAddress2: '',
    city: address?.city || '',
    state: address?.state || '',
    zip: address?.zip || '',
    country: address?.country || '',
    latitude: address?.latitude ? Number(address.latitude) : null,
    longitude: address?.longitude ? Number(address.longitude) : null,
  });
};

onMounted(() => {
  const el = document.querySelector(`#country-${props.pageId} input`);
  if (el) {
    new Cleave(el as HTMLInputElement, {
      blocks: [2],
      delimiter: '',
      uppercase: true,
    });
  }
});
</script>

<style lang="scss">
.address-section {
  button.home {
    height: 30px;
    width: 30px !important;
    border-radius: 50%;
    padding: 0 !important;
    font-size: 16px !important;
    background: 0 0;
    color: #66615b;
    border-width: 2px;
    border-style: solid;
    cursor: pointer;

    &:hover,
    &:focus {
      outline-color: #41b883;
      color: #41b883;
      border-color: #41b883;
    }

    @media (max-width: 576px) {
      margin-left: -10px;
    }
  }
}
</style>
